import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import '../module_mechanics/Styles/FormList.css';

const API_URL = 'https://ourzestapp.online/oz_backend/solar_module/solar_gatepass.php';
const useOurzestAppearance = (cssPrefix) => {
    const [themeColor, setThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [language, setLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const applyTheme = useCallback((value) => {
        const color = /^#[0-9a-f]{6}$/i.test(value || '') ? value : '#6366f1';
        const root = document.documentElement;
        root.style.setProperty(`--${cssPrefix}-primary`, color);
        root.style.setProperty(`--${cssPrefix}-primary-dark`, color);
        root.style.setProperty(`--${cssPrefix}-primary-light`, color);
        setThemeColor(color);
    }, [cssPrefix]);
    useEffect(() => {
        const sync = () => {
            applyTheme(localStorage.getItem('ourzest_theme') || '#6366f1');
            setDarkMode(localStorage.getItem('ourzest_darkMode') === 'true');
            setLanguage(localStorage.getItem('ourzest_language') || 'en');
        };
        const onStorage = event => ['ourzest_theme', 'ourzest_darkMode', 'ourzest_language'].includes(event.key) && sync();
        const onTheme = event => applyTheme(event.detail?.theme || localStorage.getItem('ourzest_theme') || '#6366f1');
        const onDark = event => setDarkMode(event.detail?.darkMode ?? localStorage.getItem('ourzest_darkMode') === 'true');
        const onLanguage = event => setLanguage(event.detail?.language || localStorage.getItem('ourzest_language') || 'en');
        sync();
        window.addEventListener('storage', onStorage);
        window.addEventListener('themeChanged', onTheme);
        window.addEventListener('darkModeToggled', onDark);
        window.addEventListener('languageChanged', onLanguage);
        const timer = window.setInterval(sync, 1000);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('themeChanged', onTheme);
            window.removeEventListener('darkModeToggled', onDark);
            window.removeEventListener('languageChanged', onLanguage);
            window.clearInterval(timer);
        };
    }, [applyTheme]);
    useEffect(() => {
        document.body.classList.toggle(`${cssPrefix}-dark`, darkMode);
        document.documentElement.classList.toggle(`${cssPrefix}-dark`, darkMode);
    }, [cssPrefix, darkMode]);
    return { themeColor, darkMode, language, isRTL: language === 'ur' || language === 'ar' };
};

const emptyForm = () => ({
    driver_name: '', driver_phone: '', vehicle_no: '', selection_mode: 'multiple',
    selected_products: [], total_quantity: '', total_bundle: '', checked_by: '', approved_by: ''
});

export default function SolarGatepass() {
    const { user, currentShop } = useAuth();
    const [form, setForm] = useState(emptyForm);
    const [products, setProducts] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [notice, setNotice] = useState(null);
    const [lastSerial, setLastSerial] = useState('');
    const { darkMode, isRTL } = useOurzestAppearance('ozui');

    const partyName = currentShop?.shop_name || currentShop?.name || '';

    const loadProducts = useCallback(async () => {
        if (!currentShop?.shop_id) return;
        setLoadingProducts(true);
        try {
            const response = await fetch(API_URL, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_products', shop_id: currentShop.shop_id, username: user?.username || '' })
            });
            const data = await response.json();
            if (!response.ok || data.status !== 'success') throw new Error(data.message || 'Unable to load products');
            setProducts((data.products || []).map(p => ({ ...p, id: Number(p.id) })));
        } catch (error) {
            setNotice({ type: 'error', text: error.message });
            setProducts([]);
        } finally { setLoadingProducts(false); }
    }, [currentShop?.shop_id, user?.username]);

    useEffect(() => { loadProducts(); }, [loadProducts]);
    useEffect(() => { setForm(emptyForm()); setLastSerial(''); setErrors({}); }, [currentShop?.shop_id]);

    const filteredProducts = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        return term ? products.filter(p => p.product_name?.toLowerCase().includes(term)) : products;
    }, [products, productSearch]);

    const update = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const toggleProduct = (id) => {
        setForm(prev => {
            const selected = prev.selected_products.includes(id);
            const selected_products = prev.selection_mode === 'single'
                ? [id]
                : selected ? prev.selected_products.filter(value => value !== id) : [...prev.selected_products, id];
            return { ...prev, selected_products };
        });
        setErrors(prev => ({ ...prev, selected_products: '' }));
    };

    const validate = () => {
        const next = {};
        if (!currentShop?.shop_id || !partyName) next.party_name = 'Select a shop first';
        if (!form.driver_name.trim()) next.driver_name = 'Driver name is required';
        if (form.driver_phone && !/^[0-9+()\-\s]{7,20}$/.test(form.driver_phone)) next.driver_phone = 'Enter a valid phone number';
        if (!form.vehicle_no.trim()) next.vehicle_no = 'Vehicle number is required';
        if (!form.selected_products.length) next.selected_products = 'Select at least one product';
        if (Number(form.total_quantity) < 0) next.total_quantity = 'Quantity cannot be negative';
        if (Number(form.total_bundle) < 0) next.total_bundle = 'Bundle cannot be negative';
        if (!form.checked_by.trim()) next.checked_by = 'Checked By is required';
        if (!form.approved_by.trim()) next.approved_by = 'Approved By is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!validate()) return;
        setSaving(true); setNotice(null);
        try {
            const response = await fetch(API_URL, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add', shop_id: currentShop.shop_id, party_name: partyName,
                    username: user?.username || '', ...form,
                    total_quantity: Number(form.total_quantity || 0), total_bundle: Number(form.total_bundle || 0)
                })
            });
            const data = await response.json();
            if (!response.ok || data.status !== 'success') throw new Error(data.message || 'Unable to create gate pass');
            setLastSerial(data.serial_number || '');
            setNotice({ type: 'success', text: `Gate pass ${data.serial_number || ''} created successfully` });
            setForm(emptyForm()); setProductSearch('');
        } catch (error) { setNotice({ type: 'error', text: error.message }); }
        finally { setSaving(false); }
    };

    const field = (name, label, inputProps = {}) => (
        <div className={`ozui-field ${errors[name] ? 'ozui-error' : ''}`}>
            <div className="ozui-input-wrapper">
                <input id={`gatepass_${name}`} name={name} placeholder=" " {...inputProps} />
                <label htmlFor={`gatepass_${name}`}>{label}</label>
            </div>
            {errors[name] && <small>{errors[name]}</small>}
        </div>
    );

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation"><div className="gradient-orb orb-1"></div><div className="gradient-orb orb-2"></div><div className="gradient-orb orb-3"></div></div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left"><div className="ozui-header-title"><h2>Solar Gate Pass</h2><p>Create an outgoing product gate pass</p></div></div>
                    <div className="ozui-header-right"><div className="ozui-header-actions"><button type="button" className="ozui-action-btn clear-draft" onClick={() => { setForm(emptyForm()); setErrors({}); }}>Clear</button></div></div>
                </div>
            </div>
            {notice && <div className={`ozui_toast-notification ${notice.type}`}><div className="ozui_toast-content"><div className="ozui_toast-text"><div className="ozui_toast-title">{notice.type === 'success' ? 'Success' : 'Error'}</div><div className="ozui_toast-message">{notice.text}</div></div></div></div>}
            <form onSubmit={submit} className="ozui-form">
                <div className="ozui-form-row">
                    {field('serial_number', 'Auto Serial Number', { value: lastSerial || 'Generated automatically on save', readOnly: true })}
                    {field('party_name', 'Party Name (Current Shop)', { value: partyName, readOnly: true })}
                    {field('driver_name', 'Driver Name *', { value: form.driver_name, onChange: e => update('driver_name', e.target.value) })}
                    {field('driver_phone', 'Driver Ph No', { value: form.driver_phone, onChange: e => update('driver_phone', e.target.value) })}
                    {field('vehicle_no', 'Vehicle No *', { value: form.vehicle_no, onChange: e => update('vehicle_no', e.target.value) })}
                    <div className="ozui-field"><div className="ozui-input-wrapper"><select id="gatepass_selection_mode" value={form.selection_mode} onChange={e => setForm(prev => ({ ...prev, selection_mode: e.target.value, selected_products: e.target.value === 'single' ? prev.selected_products.slice(0, 1) : prev.selected_products }))}><option value="single">Single Product</option><option value="multiple">Multiple Products</option></select><label htmlFor="gatepass_selection_mode">Product Selection</label></div></div>
                    {field('total_quantity', 'Total Quantity', { type: 'number', min: '0', step: '0.001', value: form.total_quantity, onChange: e => update('total_quantity', e.target.value) })}
                    {field('total_bundle', 'Total Bundle', { type: 'number', min: '0', step: '0.001', value: form.total_bundle, onChange: e => update('total_bundle', e.target.value) })}
                    {field('checked_by', 'Checked By *', { value: form.checked_by, onChange: e => update('checked_by', e.target.value) })}
                    {field('approved_by', 'Approved By *', { value: form.approved_by, onChange: e => update('approved_by', e.target.value) })}
                </div>
                <div className={`ozui-field ${errors.selected_products ? 'ozui-error' : ''}`}>
                    <div className="ozui-input-wrapper ozui-category-wrapper"><input type="text" value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder=" " /><label>Search Product Name ({form.selected_products.length} selected)</label></div>
                    <div className="ozui-category-dropdown" style={{ position: 'relative', display: 'block', maxHeight: '280px', overflowY: 'auto' }}>
                        {loadingProducts ? <div className="ozui_loading-state">Loading products...</div> : filteredProducts.length ? filteredProducts.map(product => <div key={product.id} className={`ozui-category-option ${form.selected_products.includes(product.id) ? 'ozui-selected' : ''}`} onClick={() => toggleProduct(product.id)}><input type={form.selection_mode === 'single' ? 'radio' : 'checkbox'} checked={form.selected_products.includes(product.id)} readOnly /><span>{product.product_name}</span></div>) : <div className="ozui-category-empty">No products found</div>}
                    </div>
                    {errors.selected_products && <small>{errors.selected_products}</small>}
                </div>
                <div className="ozui-form-actions"><button type="button" className="ozui-btn" onClick={() => setForm(emptyForm())}>Clear</button><button type="submit" className="ozui-btn ozui-btn-primary" disabled={saving || !currentShop}>{saving ? 'Saving...' : 'Save Gate Pass'}</button></div>
            </form>
        </div>
    );
}
